import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';

// Global reference to context functions (for use outside React components like Apollo)
let globalPermissionDeniedActions: {
  showPermissionDenied: () => void;
  hidePermissionDenied: () => void;
} | null = null;

interface PermissionDeniedContextType {
  permissionDeniedState: boolean;
  showPermissionDenied: () => void;
  hidePermissionDenied: () => void;
}

const PermissionDeniedContext = createContext<PermissionDeniedContextType | null>(null);

interface PermissionDeniedProviderProps {
  children: ReactNode;
}

export const PermissionDeniedProvider: React.FC<PermissionDeniedProviderProps> = ({ children }) => {
  const [state, setState] = useState(false);

  const showPermissionDenied = useCallback(() => {
    setState(true);
  }, []);

  const hidePermissionDenied = useCallback(() => {
    setState(false);
  }, []);

  const value: PermissionDeniedContextType = {
    permissionDeniedState: state,
    showPermissionDenied,
    hidePermissionDenied,
  };

  // Update global reference whenever context functions change
  useEffect(() => {
    globalPermissionDeniedActions = {
      showPermissionDenied,
      hidePermissionDenied,
    };

    // Cleanup on unmount
    return () => {
      globalPermissionDeniedActions = null;
    };
  }, [showPermissionDenied, hidePermissionDenied]);

  return <PermissionDeniedContext.Provider value={value}>{children}</PermissionDeniedContext.Provider>;
};

export const usePermissionDenied = (): PermissionDeniedContextType => {
  const context = useContext(PermissionDeniedContext);
  if (!context) {
    throw new Error('usePermissionDenied must be used within a PermissionDeniedProvider');
  }
  return context;
};

// Global function for use outside React components (e.g., Apollo error handlers)
export const showGlobalPermissionDenied = () => {
  if (globalPermissionDeniedActions) {
    globalPermissionDeniedActions.showPermissionDenied();
  } else {
    console.warn('PermissionDeniedProvider not available. Cannot show permission denied modal.');
  }
};
